import Script from 'next/script';
import { OrganizationJsonLd } from 'next-seo';

import type { Country, Locale } from '@/types/regionTypes';

import type { StoreHit } from '@/integrations/algolia/types';
import {
  Brand,
  getBrandName,
  toBrandEnum,
} from '@/lib/configuration/configuration';
import { BRAND } from '@/lib/const';

import { convertToOpeningHoursSpecification } from './Functions';

interface GeoCoordinates {
  '@type': 'GeoCoordinates';
  latitude: number;
  longitude: number;
}

interface PostalAddress {
  addressCountry: string;
  addressLocality: string;
  postalCode: string;
  streetAddress: string;
}

interface ContactPoint {
  '@type': 'ContactPoint';
  areaServed: string;
  availableLanguage: string[];
  contactType: string;
  telephone: string;
}

interface LocalBusiness {
  address: PostalAddress;
  contactPoint: ContactPoint;
  description: string;
  email: string;
  geo?: GeoCoordinates;
  image: string;
  name: string;
  openingHours: StoreHit['hours'];
  priceRange: string;
  sameAs: string[];
  telephone: string;
  url: string;
}

export const generateLocalBusinessSchema = (
  data: StoreHit,
  locale: string,
  domain = 'https://www.breitling.com/',
): LocalBusiness => {
  const country = data.country_code?.toLowerCase();
  const latitude = data._geoloc?.lat;
  const longitude = data._geoloc?.lng;
  const geo =
    typeof latitude === 'number' && typeof longitude === 'number'
      ? { '@type': 'GeoCoordinates' as const, latitude, longitude }
      : undefined;

  return {
    name: data.name ?? '',
    description: data.description ?? '',
    image: data.thumbnail_url ?? '',
    telephone: data.phone ?? '',
    email: data.emails?.length ? data.emails[0] : '',
    address: {
      streetAddress: data.street2
        ? `${data.street} ${data.street2}`
        : (data.street ?? ''),
      addressLocality: data.city ?? '',
      postalCode: data.postal_code ?? '',
      addressCountry: data.country_code ?? '',
    },
    ...(geo ? { geo } : {}),
    url: `${domain}${locale}/stores/breitling-boutiques/${country}/${data.slug}`,
    sameAs: [
      'https://www.facebook.com/Breitling',
      'https://twitter.com/Breitling',
      'https://www.instagram.com/breitling',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-866-657-7670',
      contactType: 'Customer Service',
      areaServed: 'US',
      availableLanguage: ['English'],
    },
    priceRange: '$$$',
    openingHours: data.hours ?? '',
  };
};

export const buildBoutiqueStoreJsonLd = (
  data: LocalBusiness,
): Record<string, unknown> => {
  const openingHoursSpecification =
    data.openingHours && typeof data.openingHours === 'object'
      ? convertToOpeningHoursSpecification(
          data.openingHours as Parameters<
            typeof convertToOpeningHoursSpecification
          >[0],
        )
      : [];

  return {
    '@context': 'https://schema.org',
    '@type': 'Store',
    '@id': data.url,
    name: data.name,
    description: data.description,
    image: [data.image],
    telephone: data.telephone,
    email: data.email,
    url: data.url,
    address: {
      '@type': 'PostalAddress',
      streetAddress: data.address.streetAddress,
      addressLocality: data.address.addressLocality,
      postalCode: data.address.postalCode,
      addressCountry: data.address.addressCountry,
    },
    ...(data.geo ? { geo: data.geo } : {}),
    sameAs: data.sameAs,
    contactPoint: data.contactPoint,
    priceRange: data.priceRange,
    ...(openingHoursSpecification.length > 0
      ? { openingHoursSpecification }
      : {}),
  };
};

export function makeWebsiteJsonLd(
  brand: Brand,
  country: Country,
  locale: Locale,
) {
  const jsonLdData = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: getBrandName(brand),
    url: `${process.env.NEXT_PUBLIC_URL}${country.slug}-${locale.slug}/`,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${process.env.NEXT_PUBLIC_URL}${country.slug}-${locale.slug}/?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  });

  const WebsiteJsonLd: () => JSX.Element = () => (
    <Script id='websiteJsonLd' type='application/ld+json'>
      {jsonLdData}
    </Script>
  );

  return WebsiteJsonLd;
}

type OrganizationData = {
  contactPoint: {
    areaServed: string;
    availableLanguage: string[];
    contactType: string;
    telephone: string;
  }[];
  logo: string;
  name: string;
  sameAs: string[];
  url: string;
};

const makeBrandOrganisationData = (
  brand: Brand,
  country: Country,
  locale: Locale,
): OrganizationData => {
  switch (brand) {
    case Brand.Gallet:
      return {
        logo: process.env.NEXT_PUBLIC_URL + 'images/gallet/logo.svg',
        name: getBrandName(brand),
        sameAs: [
          'https://www.facebook.com/Breitling',
          'https://x.com/Breitling',
          'https://www.instagram.com/breitling',
          'https://www.linkedin.com/company/breitling',
        ],
        url: `${process.env.NEXT_PUBLIC_URL}${country.slug}-${locale.slug}/?q={search_term_string}`,
        contactPoint: [
          {
            telephone: '+1-866-657-7670',
            contactType: 'Customer Service',
            areaServed: 'US',
            availableLanguage: ['English'],
          },
        ],
      };
    default:
      return {
        logo: 'https://www.breitling.com/assets/images/corporate/asset-version-686e4b342e/breitling_logo_lg.svg',
        name: getBrandName(brand),
        sameAs: [
          'https://www.facebook.com/Breitling',
          'https://x.com/Breitling',
          'https://www.instagram.com/breitling',
          'https://www.linkedin.com/company/breitling',
        ],
        url: `${process.env.NEXT_PUBLIC_URL}${country.slug}-${locale.slug}/?q={search_term_string}`,
        contactPoint: [
          {
            telephone: '+1-866-657-7670',
            contactType: 'Customer Service',
            areaServed: 'US',
            availableLanguage: ['English'],
          },
        ],
      };
  }
};

export function makeOrganizationJsonLdScript(
  brand: Brand,
  country: Country,
  locale: Locale,
) {
  const JsonLdComponent: () => JSX.Element = () => (
    <OrganizationJsonLd
      {...makeBrandOrganisationData(brand, country, locale)}
    />
  );
  return JsonLdComponent;
}

export const CmsViewStructuredData = ({
  cmsPageSlug,
  country,
  locale,
}: {
  cmsPageSlug: string;
  country: Country;
  locale: Locale;
}) => {
  const isHomePage = cmsPageSlug === '/';
  if (!isHomePage) {
    return null;
  }
  const WebsiteJsonLdScript = makeWebsiteJsonLd(
    toBrandEnum(BRAND),
    country,
    locale,
  );
  const OrganizationJsonLdScript = makeOrganizationJsonLdScript(
    toBrandEnum(BRAND),
    country,
    locale,
  );
  return (
    <>
      <WebsiteJsonLdScript />
      <OrganizationJsonLdScript />
    </>
  );
};
